/**
 * QCVerificationForm Component
 * Dialog-based form for submitting QC verification data.
 *
 * Features:
 * - Moisture and purity input with sliders and numeric fields
 * - Real-time grade calculation preview
 * - Notes textarea (500 char limit)
 * - Submit button with loading state
 * - Form validation with Zod
 * - Dung type is automatically used from the transaction
 *
 * @description
 * This component provides a comprehensive QC verification form that appears
 * as a dialog. It integrates with the GradeCalculator component
 * to show real-time grade preview as users adjust quality measurements.
 * The dung type is already known from the transaction, so no selection is needed.
 *
 * @author SAUBHAGYA Development Team
 * @since Story 3-4
 */

import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Loader2, AlertCircle } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Slider } from '@/components/ui/slider';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Alert, AlertDescription } from '@/components/ui/alert';

import { GradeCalculator } from '../display/GradeCalculator';
import { useSubmitQC } from '@/hooks/useTransactions';
import { TransactionDTO } from '@/services/manufacturing/manufacturingApi';

// Form validation schema
const qcVerificationSchema = z.object({
  moisturePercent: z
    .number()
    .min(0, 'Moisture must be at least 0%')
    .max(100, 'Moisture cannot exceed 100%'),
  purityPercent: z
    .number()
    .min(0, 'Purity must be at least 0%')
    .max(100, 'Purity cannot exceed 100%'),
  notes: z
    .string()
    .max(500, 'Notes cannot exceed 500 characters')
    .optional(),
});

type QCVerificationFormValues = z.infer<typeof qcVerificationSchema>;

interface QCVerificationFormProps {
  /** Whether the sheet is open */
  open: boolean;
  /** Callback when sheet should close */
  onOpenChange: (open: boolean) => void;
  /** Transaction to verify */
  transaction: TransactionDTO | null;
  /** Callback when QC is successfully submitted */
  onSuccess?: (transaction: TransactionDTO) => void;
}

export function QCVerificationForm({
  open,
  onOpenChange,
  transaction,
  onSuccess,
}: QCVerificationFormProps) {
  const [error, setError] = useState<string | null>(null);

  // QC submission mutation
  const submitQC = useSubmitQC();

  // Form setup
  const form = useForm<QCVerificationFormValues>({
    resolver: zodResolver(qcVerificationSchema),
    defaultValues: {
      moisturePercent: 25,
      purityPercent: 85,
      notes: '',
    },
  });

  // Watch values for real-time grade calculation
  const moisturePercent = form.watch('moisturePercent');
  const purityPercent = form.watch('purityPercent');

  // Reset form when transaction changes
  useEffect(() => {
    if (transaction && open) {
      form.reset({
        moisturePercent: 25,
        purityPercent: 85,
        notes: '',
      });
      setError(null);
    }
  }, [transaction, open, form]);

  // Handle form submission
  const onSubmit = async (values: QCVerificationFormValues) => {
    if (!transaction) return;

    setError(null);

    try {
      const result = await submitQC.mutateAsync({
        transactionId: transaction.id,
        data: {
          moisturePercent: values.moisturePercent,
          purityPercent: values.purityPercent,
          notes: values.notes || undefined,
        },
      });

      onSuccess?.(result);
      onOpenChange(false);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to submit QC verification';
      setError(errorMessage);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>QC Verification</DialogTitle>
          <DialogDescription>
            {transaction
              ? `Verify quality for batch ${transaction.batchCode}`
              : 'Select a transaction to verify'}
          </DialogDescription>
        </DialogHeader>

        {transaction && (
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="space-y-6 py-6"
            >
              {/* Error Alert */}
              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              {/* Transaction Info */}
              <div className="rounded-lg border p-4 space-y-2 bg-muted/30">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Batch Code</span>
                  <span className="font-mono font-medium">
                    {transaction.batchCode}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Quantity</span>
                  <span className="font-medium">
                    {transaction.quantityKg} kg
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Material Type</span>
                  <span className="font-medium">
                    {transaction.rawMaterialTypeName}
                  </span>
                </div>
              </div>

              {/* Moisture Percent */}
              <FormField
                control={form.control}
                name="moisturePercent"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Moisture Content (%)</FormLabel>
                    <FormControl>
                      <div className="space-y-3">
                        <div className="flex items-center gap-4">
                          <Slider
                            min={0}
                            max={100}
                            step={0.5}
                            value={[field.value]}
                            onValueChange={(value) => field.onChange(value[0])}
                            className="flex-1"
                          />
                          <Input
                            type="number"
                            min={0}
                            max={100}
                            step={0.01}
                            value={field.value}
                            onChange={(e) =>
                              field.onChange(parseFloat(e.target.value) || 0)
                            }
                            className="w-20"
                          />
                        </div>
                      </div>
                    </FormControl>
                    <FormDescription>
                      Lower moisture indicates better quality (Grade A: &le;30%)
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Purity Percent */}
              <FormField
                control={form.control}
                name="purityPercent"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Purity (%)</FormLabel>
                    <FormControl>
                      <div className="space-y-3">
                        <div className="flex items-center gap-4">
                          <Slider
                            min={0}
                            max={100}
                            step={0.5}
                            value={[field.value]}
                            onValueChange={(value) => field.onChange(value[0])}
                            className="flex-1"
                          />
                          <Input
                            type="number"
                            min={0}
                            max={100}
                            step={0.01}
                            value={field.value}
                            onChange={(e) =>
                              field.onChange(parseFloat(e.target.value) || 0)
                            }
                            className="w-20"
                          />
                        </div>
                      </div>
                    </FormControl>
                    <FormDescription>
                      Higher purity indicates better quality (Grade A: &ge;90%)
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Real-time Grade Calculator */}
              <GradeCalculator
                moisturePercent={moisturePercent}
                purityPercent={purityPercent}
              />

              {/* Notes */}
              <FormField
                control={form.control}
                name="notes"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Notes (Optional)</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Add any observations or special notes..."
                        className="min-h-[100px] resize-none"
                        maxLength={500}
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>
                      {(field.value?.length || 0)}/500 characters
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Submit Button */}
              <DialogFooter className="pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                  disabled={submitQC.isPending}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={submitQC.isPending}>
                  {submitQC.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    'Submit QC Verification'
                  )}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default QCVerificationForm;
